import React, { useState } from 'react';
import {
  Box,
  Typography,
  MenuItem,
  FormControl,
  Select,
  InputLabel,
  Button,
  Container,
  Paper
} from '@mui/material';
import SpreadsheetUpload from '../components/SpreadsheetUpload';
import { submitJob } from '../api';

interface Mapping {
  SheetAColumn: string;
  SheetAColumnIndex: number;
  SheetBColumn: string;
  SheetBColumnIndex: number;
}

const StandardComparison: React.FC = () => {
  const [columnsA, setColumnsA] = useState<string[]>([]);
  const [columnsB, setColumnsB] = useState<string[]>([]);
  const [selectedA, setSelectedA] = useState('');
  const [selectedB, setSelectedB] = useState('');
  const [mappings, setMappings] = useState<Mapping[]>([]);
  const [sheetAFile, setSheetAFile] = useState<File | null>(null);
  const [sheetBFile, setSheetBFile] = useState<File | null>(null);

  const handleUpload = (
      sheetA: File | null,
      sheetB: File | null,
      colsA: string[],
      colsB: string[],
  ) => {
    setSheetAFile(sheetA);
    setSheetBFile(sheetB);
    setColumnsA(colsA);
    setColumnsB(colsB);
    setSelectedA('');
    setSelectedB('');
    setMappings([]);
  };

  const addMapping = () => {
    if (!selectedA || !selectedB) return;

    const SheetAColumnIndex = columnsA.indexOf(selectedA);
    const SheetBColumnIndex = columnsB.indexOf(selectedB);

    const newMapping: Mapping = {
      SheetAColumn: selectedA,
      SheetAColumnIndex,
      SheetBColumn: selectedB,
      SheetBColumnIndex,
    };

    setMappings((prev) => [...prev, newMapping]);
    setSelectedA('');
    setSelectedB('');
  };

  const handleCompareClick = async () => {
    if (!sheetAFile || !sheetBFile) {
      alert('Please upload both sheets.');
      return;
    }

    try {
      await submitJob(
        '/api/compare',
        { sheetA: sheetAFile, sheetB: sheetBFile },
        { mappings: JSON.stringify(mappings) },
        'comparison_result.xlsx',
      );
      console.log('Comparison file downloaded successfully.');
    } catch (err) {
      console.error('Error during comparison:', err);
      alert('An error occurred while comparing the sheets. Please try again.');
    }
  };

  // const availableColumnsA = columnsA.filter(col => !mappings.some(mapping => mapping.SheetAColumn === col));
  // const availableColumnsB = columnsB.filter(col => !mappings.some(mapping => mapping.SheetBColumn === col));

  const availableColumnsA = columnsA
      .filter(col => !mappings.some(mapping => mapping.SheetAColumn === col))
      .sort((a, b) => a.localeCompare(b));

  const availableColumnsB = columnsB
      .filter(col => !mappings.some(mapping => mapping.SheetBColumn === col))
      .sort((a, b) => a.localeCompare(b));


  // const sheetAName = sheetAFile?.name || 'Sheet A';
  // const sheetBName = sheetBFile?.name || 'Sheet B';

  const sheetAName = sheetAFile?.name.replace(/\.[^/.]+$/, '') || 'Sheet A';
  const sheetBName = sheetBFile?.name.replace(/\.[^/.]+$/, '') || 'Sheet B';


  return (
      <Container maxWidth="lg" sx={{ textAlign: 'center', paddingTop: '50px' }}>
        <Box sx={{ backgroundColor: '#183B4E', color: 'white', padding: '20px', borderRadius: '8px' }}>
          <Typography variant="h3" sx={{ fontWeight: 'bold', marginBottom: '20px' }}>
            Standard Comparison
          </Typography>
          <Typography variant="h6" sx={{ fontStyle: 'italic', marginBottom: '40px' }}>
            Match columns and compare the sheets automatically.
          </Typography>
        </Box>

        <Paper
            elevation={6}
            sx={{
              padding: '30px',
              borderRadius: '8px',
              marginTop: '30px',
              backgroundColor: '#f5f5f5',
              textAlign: 'left',
            }}
        >

          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <SpreadsheetUpload onUpload={handleUpload} />
          </Box>

          {columnsA.length > 0 && columnsB.length > 0 && (
              <Box sx={{ mt: 4 }}>
                <Typography variant="h6" sx={{ fontWeight: 'medium', marginBottom: '10px', textAlign: 'center' }}>
                  SELECT COLUMNS TO MAP
                </Typography>

                <Box sx={{ display: 'flex', gap: 4, mt: 2 }}>
                  <FormControl fullWidth>
                    <InputLabel>{sheetAName} Column</InputLabel>
                    <Select
                        value={selectedA}
                        label={`${sheetAName} Column`}
                        onChange={(e) => setSelectedA(e.target.value)}
                    >
                      {availableColumnsA.map((col) => (
                          <MenuItem key={col} value={col}>
                            {col}
                          </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth>
                    <InputLabel>{sheetBName} Column</InputLabel>
                    <Select
                        value={selectedB}
                        label={`${sheetBName} Column`}
                        onChange={(e) => setSelectedB(e.target.value)}
                    >
                      {availableColumnsB.map((col) => (
                          <MenuItem key={col} value={col}>
                            {col}
                          </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <Box sx={{ textAlign: 'center', mt: 2 }}>
                  <Button variant="outlined" onClick={addMapping}>
                    Add Mapping
                  </Button>
                </Box>

                {mappings.length > 0 && (
                    <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                      <Paper
                          elevation={3}
                          sx={{ padding: 3, borderRadius: 2, backgroundColor: '#f5f5f5', width: '100%', maxWidth: 600 }}
                      >
                        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, textAlign: 'center' }}>
                          SELECTED COLUMN MAPPING
                        </Typography>

                        <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead>
                          <tr>
                            <th style={{ textAlign: 'left', padding: '8px' }}>{sheetAName}</th>
                            <th style={{ textAlign: 'center', padding: '8px' }}></th>
                            <th style={{ textAlign: 'right', padding: '8px' }}>{sheetBName}</th>
                          </tr>
                          </thead>
                          <tbody>
                          {mappings.map((m, i) => (
                              <tr key={i}>
                                <td style={{ padding: '8px', textAlign: 'left' }}>{m.SheetAColumn}</td>
                                <td style={{ padding: '8px', textAlign: 'center' }}>⇄</td>
                                <td style={{ padding: '8px', textAlign: 'right' }}>{m.SheetBColumn}</td>
                              </tr>
                          ))}
                          </tbody>
                        </Box>

                        <Typography
                            variant="body1"
                            sx={{
                              mt: 3,
                              textAlign: 'center',
                              textTransform: 'uppercase',
                              fontWeight: 'bold',
                              backgroundColor: '#FFEB3B',
                              padding: '8px 16px',
                              borderRadius: '4px',
                            }}
                        >
                          Once you've selected the necessary column pairs for comparison, click below:
                        </Typography>

                        <Box sx={{ textAlign: 'center', mt: 2 }}>
                          <Button
                              variant="contained"
                              color="primary"
                              sx={{ backgroundColor: '#27548A' }}
                              onClick={handleCompareClick}
                          >
                            Compare
                          </Button>
                        </Box>
                      </Paper>
                    </Box>
                )}
              </Box>
          )}
        </Paper>
      </Container>
  );
};

export default StandardComparison;